/**
 * Money, shown exactly.
 *
 * There were three formatters. The order summary rounded to whole kroner, the
 * pay button always printed two decimals, and the shipping list used the
 * locale default. So a basket of 1 299,50 plus 79,00 delivery showed
 *
 *     Ordrebeløp        1 379 kr        ← rounded up
 *     1299,50 ×1        1 300 kr        ← rounded up
 *     Betal 1 378,50 NOK                ← the real amount
 *
 * The customer is told two different totals on one screen, and the larger one
 * is the one that is wrong. Rounding a price the customer is about to be
 * charged is not a display detail — it is the shop appearing to overcharge,
 * and it is the sort of thing people take a screenshot of.
 *
 * One rule everywhere: never round, and show decimals only when the amount has
 * them. 1 300 stays "1 300 kr"; 1 299,50 stays "1 299,50 kr".
 */

/** Language tag for a checkout locale. Shared so the three call sites agree. */
export function numberLocaleFor(sdkLocale: string | undefined): string {
  switch (sdkLocale) {
    case 'no': return 'nb-NO';
    case 'sv': return 'sv-SE';
    case 'da': return 'da-DK';
    case 'fi': return 'fi-FI';
    default: return 'en-US';
  }
}

/** True when the amount is not a whole unit of currency — i.e. it has øre. */
function hasFraction(minor: number): boolean {
  return Math.round(minor) % 100 !== 0;
}

/**
 * Format an amount held in minor units (øre) for display.
 *
 * @param minor    the amount, in øre
 * @param currency ISO 4217, used for the symbol
 * @param locale   language tag from numberLocaleFor()
 */
export function formatMoney(minor: number, currency: string, locale: string): string {
  const digits = hasFraction(minor) ? 2 : 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minor / 100);
}

/**
 * The same rule without a currency symbol, for places that print their own.
 */
export function formatAmount(minor: number, locale: string): string {
  const digits = hasFraction(minor) ? 2 : 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minor / 100);
}
